export const STAGE_LABELS: Record<string, string> = {
  NEW: "New Request",
  QUALIFYING: "Contact Referrer",
  MATCHING: "Fit Review",
  TOUR_SCHEDULED: "Tour Scheduled",
  PENDING_DECISION: "Decision",
  PLACED: "Placed",
  LOST: "Lost",
  CANCELLED: "Cancelled",
  CLOSED: "Closed",
  WITHDRAWN: "Withdrawn",
};

export const TERMINAL_STAGE_SET = new Set([
  "LOST",
  "CANCELLED",
  "CLOSED",
  "WITHDRAWN",
]);

export function parseExtra(raw: any): Record<string, any> {
  if (!raw) return {};
  if (typeof raw === "object") {
    try {
      return JSON.parse(JSON.stringify(raw));
    } catch {
      return { ...raw };
    }
  }
  try {
    return JSON.parse(String(raw));
  } catch {
    return {};
  }
}

export function stringifyExtraIfNeeded(oldExtra: any, obj: any) {
  return typeof oldExtra === "string" ? JSON.stringify(obj) : obj;
}

export function stringifySeedIfNeeded(oldSeed: any, obj: any) {
  return typeof oldSeed === "string" ? JSON.stringify(obj) : obj;
}

export function parseBroadcastSeed(seedJson: any): Record<string, any> {
  if (!seedJson) return {};
  if (typeof seedJson === "object") {
    try {
      return JSON.parse(JSON.stringify(seedJson));
    } catch {
      return { ...seedJson };
    }
  }
  try {
    return JSON.parse(String(seedJson));
  } catch {
    return {};
  }
}

export function getLeadBroadcastGroupId(lead: any): string | null {
  const extra = parseExtra(lead?.extra);
  const groupId = extra?.broadcast?.groupId;
  return groupId ? String(groupId) : null;
}

export function formatInlineDateTime(value: any): string {
  if (!value) return "Date unknown";
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return "Date unknown";
  const datePart = d.toLocaleDateString(undefined, {
    year: "numeric",
    month: "short",
    day: "2-digit",
  });
  const timePart = d.toLocaleTimeString(undefined, {
    hour: "2-digit",
    minute: "2-digit",
    hour12: true,
  });
  return `${datePart} ${timePart}`;
}

export function getAgentRequestTone(status?: string | null) {
  switch (String(status || "").toUpperCase()) {
    case "REQUESTED":
      return {
        headerBg: "rgba(255, 250, 244, 0.96)",
        bodyBg: "#ffffff",
      };
    case "ASSIGNED":
      return {
        headerBg: "rgba(247, 251, 255, 0.96)",
        bodyBg: "#ffffff",
      };
    default:
      return {
        headerBg: "rgba(248, 250, 252, 0.96)",
        bodyBg: "#ffffff",
      };
  }
}

export function getCompensationSummary(raw: any, extraObj: Record<string, any>) {
  const summary = raw?.compensationSummary || {};
  const extraComp = extraObj?.compensation || {};
  const compensationPercentRaw =
    summary?.compensationPercent ??
    raw?.compensationPercent ??
    extraComp?.compensationPercent ??
    extraComp?.percent ??
    null;
  const compensationPercent =
    compensationPercentRaw == null || compensationPercentRaw === ""
      ? null
      : Number(compensationPercentRaw);
  return {
    agreementLabel:
      String(summary?.agreementLabel || "").trim() || "Agent Compensation Agreement",
    compensationPercent:
      Number.isFinite(compensationPercent as number) ? compensationPercent : null,
    compensationSourceType:
      String(
        summary?.compensationSourceType ||
          raw?.compensationSourceType ||
          extraComp?.compensationSourceType ||
          "PENDING_ASSIGNMENT",
      )
        .trim()
        .toUpperCase(),
    compensationLockedAt:
      summary?.compensationLockedAt ||
      raw?.compensationLockedAt ||
      extraComp?.compensationLockedAt ||
      null,
    compensationNotes:
      String(
        summary?.compensationNotes ||
          raw?.compensationNotes ||
          extraComp?.compensationNotes ||
          "",
      ).trim() || null,
    agentAgreementVersion:
      String(
        summary?.agentAgreementVersion ||
          raw?.agentAgreementVersion ||
          extraComp?.agentAgreementVersion ||
          "",
      ).trim() || null,
    recipientAgentName:
      String(summary?.recipientAgentName || raw?.commissionAgentName || "").trim() || null,
  };
}

export function getCompensationSourceLabel(value: string) {
  if (value === "AGENT_SOURCED") return "Agent-sourced lead";
  if (value === "PLATFORM_ASSIGNED") return "Assigned by Plany";
  return "Pending assignment";
}

export function formatCompensationLockedAt(value: any) {
  const formatDateTime = (date: Date) =>
    date.toLocaleString(undefined, {
      year: "numeric",
      month: "short",
      day: "2-digit",
      hour: "numeric",
      minute: "2-digit",
      second: "2-digit",
      hour12: true,
    });

  if (!value) return "Not locked yet";
  if (value instanceof Date) {
    return Number.isNaN(value.getTime()) ? String(value) : formatDateTime(value);
  }
  if (typeof value === "number" && Number.isFinite(value)) {
    const numericDate = new Date(value);
    return Number.isNaN(numericDate.getTime()) ? String(value) : formatDateTime(numericDate);
  }

  const raw = String(value).trim();
  if (!raw) return "Not locked yet";
  if (/^\d{10,13}$/.test(raw)) {
    const numericValue = Number(raw);
    if (Number.isFinite(numericValue)) {
      const numericDate = new Date(raw.length === 10 ? numericValue * 1000 : numericValue);
      if (!Number.isNaN(numericDate.getTime())) return formatDateTime(numericDate);
    }
  }

  const direct = new Date(raw);
  if (!Number.isNaN(direct.getTime())) return formatDateTime(direct);

  const normalized = raw.includes(" ") && !raw.includes("T") ? raw.replace(" ", "T") : raw;
  const retry = new Date(normalized);
  if (!Number.isNaN(retry.getTime())) return formatDateTime(retry);

  return raw;
}

export function shouldShowCompensationSourceChip(agreementLabel: string, sourceType: string) {
  const label = String(agreementLabel || "").trim().toLowerCase();
  const source = String(sourceType || "").trim().toUpperCase();
  if (!label) return true;
  if (source === "AGENT_SOURCED" && label.includes("agent-sourced")) return false;
  if (source === "PLATFORM_ASSIGNED" && label.includes("plany-assigned")) return false;
  return true;
}

export function getFollowUpSummary(raw: any, extraObj: Record<string, any>) {
  const summary = raw?.followUpSummary || {};
  const extraFollowUp = extraObj?.followUp || {};
  return {
    followUpStatus: String(
      summary?.followUpStatus || raw?.followUpStatus || extraFollowUp?.status || "PENDING_ASSIGNMENT",
    )
      .trim()
      .toUpperCase(),
    lastFollowUpAt:
      summary?.lastFollowUpAt || raw?.lastFollowUpAt || extraFollowUp?.lastFollowUpAt || null,
    nextFollowUpAt:
      summary?.nextFollowUpAt || raw?.nextFollowUpAt || extraFollowUp?.nextFollowUpAt || null,
    followUpNotes:
      String(summary?.followUpNotes || raw?.followUpNotes || extraFollowUp?.notes || "").trim() ||
      null,
    responsibleAgentName:
      String(
        summary?.responsibleAgentName ||
          raw?.assignedAgentName ||
          raw?.sourceAgentName ||
          extraObj?.concierge?.ownerAgentName ||
          "",
      ).trim() || null,
  };
}

function isPlaceholderAgentName(value: any) {
  const name = String(value || "").trim();
  if (!name) return true;
  return /^agent\s+\d+$/i.test(name);
}

export function normalizeAgentOption(input: any) {
  const id = String(input?.id || input?.userId || "").trim();
  if (!id) return null;
  return {
    id,
    name:
      String(input?.name || input?.username || input?.email || `Agent ${id}`).trim() ||
      `Agent ${id}`,
    email: String(input?.email || "").trim() || null,
  };
}

export function mergeAgentOption(
  current: { id: string; name: string; email?: string | null } | undefined,
  incoming: { id: string; name: string; email?: string | null },
) {
  if (!current) return incoming;
  const incomingHasBetterName =
    !isPlaceholderAgentName(incoming.name) &&
    (isPlaceholderAgentName(current.name) || !current.name);
  return {
    ...current,
    ...(incomingHasBetterName ? { name: incoming.name } : {}),
    email: current.email || incoming.email || null,
  };
}

export function hasAgentFlag(user: any) {
  return (
    user?.isAgent === true ||
    user?.isConciergeAgent === true ||
    user?.isDefaultConcierge === true
  );
}

export function applyConciergeAgentState(
  existingConcierge: any,
  nextState: {
    status?: string | null;
    ownerAgentId?: string | null;
    ownerAgentName?: string | null;
    slaAt?: string | null;
    agent?: any;
  },
) {
  const concierge = {
    ...(existingConcierge || {}),
  };
  if (nextState.status !== undefined) concierge.status = nextState.status;
  if (nextState.ownerAgentId !== undefined) concierge.ownerAgentId = nextState.ownerAgentId;
  if (nextState.ownerAgentName !== undefined) concierge.ownerAgentName = nextState.ownerAgentName;
  if (nextState.slaAt !== undefined) concierge.slaAt = nextState.slaAt;
  if (nextState.agent !== undefined) concierge.agent = nextState.agent;
  return concierge;
}
